<template>
  <div :class="$style.container">
    <labeled-input
      v-model="merchantEmail"
      v-m-b="20"
      data-test="registrationFormEmailInput"
      :label="getFieldLabel($v.merchantEmail, 'registration.email.input.label')"
      :placeholder="$t('registration.email.input.placeholder')"
      :disabled="loading"
      :error="$v.merchantEmail.$error"
      @focus="$v.merchantEmail.$reset"
      @blur="$v.merchantEmail.$touch"
    />

    <labeled-input
      v-model="merchantPhone"
      v-m-b="20"
      data-test="registrationFormPhoneInput"
      inputmode="tel"
      :label="getFieldLabel($v.merchantPhone, 'registration.phone.input.label')"
      :placeholder="$t('registration.phone.input.placeholder')"
      :force-replace="phoneReplacementRules"
      :maxlength="15"
      :disabled="loading"
      :error="$v.merchantPhone.$error"
      @focus="$v.merchantPhone.$reset"
      @blur="$v.merchantPhone.$touch"
    />

    <labeled-input
      v-if="isVisiblePromocode"
      v-model="promoCode"
      data-test="registrationFormPromocodeInput"
      :label="getFieldLabel($v.promoCode, 'registration.promo.code.input.label')"
      :placeholder="$t('registration.promo.code.input.placeholder')"
      :disabled="loading"
      :error="$v.promoCode.$error"
      @focus="$v.promoCode.$reset"
      @blur="$v.promoCode.$touch"
    >
      <template v-slot:icon="{ focus }">
        <base-button
          v-m-left="5"
          v-color="focus ? 'input-text-focus' : 'input-text'"
          data-test="registrationFormPromocodeHideButton"
          @click="isVisiblePromocode = false"
        >
          <base-icon name="basic_circle-x" />
        </base-button>
      </template>
    </labeled-input>

    <!-- TODO: @danealton доработать взаимодействие с помощью клавиатуры -->
    <div
      v-else
      data-test="registrationFormPromocodeShowButton"
      :class="$style.placeholder"
      @click="handleShownPromocode"
    >
      {{ $t('registration.promo.code.placeholder.text') }}
    </div>

    <div :class="$style.submit">
      <simple-button
        data-test="registrationFormSubmitButton"
        variant="secondary"
        :disabled="isInvalidForm"
        @click="handleSubmit"
      >
        {{ $t('common.registration') }}
      </simple-button>
    </div>
  </div>
</template>

<script>
import {
  email as emailValidator,
  required as requiredValidator,
  requiredIf as requiredIfValidator,
  minLength as minLengthValidator
} from 'vuelidate/lib/validators';

import { apiCommon } from 'lib/utils/axios';
import checkPromocode from 'api/requests/promocode/checkPromoсode';
import { getInputFieldLabel } from 'services/helpers/errorHelpers';

import BaseButton from 'atoms/base/BaseButton';
import LabeledInput from 'molecules/common/LabeledInput';
import SimpleButton from 'molecules/common/SimpleButton';

import { UNALLOWED_PHONE_SYMBOLS } from 'definitions/regexp';

const PHONE_MIN_LENGTH = 11;

export default {
  name: 'RegistrationForm',
  components: {
    BaseButton,
    LabeledInput,
    SimpleButton,
  },
  props: {
    loading: Boolean,
  },
  data() {
    return {
      merchantEmail: '',
      merchantPhone: '',
      promoCode: '',
      isVisiblePromocode: false,
      phoneReplacementRules: [UNALLOWED_PHONE_SYMBOLS, ''],
    };
  },
  validations() {
    if (this.isVisiblePromocode) {
      return {
        merchantEmail: {
          required: requiredValidator,
          email: emailValidator,
        },
        merchantPhone: {
          required: requiredValidator,
          minLength: minLengthValidator(PHONE_MIN_LENGTH),
        },
        promoCode: {
          required: requiredIfValidator('isVisiblePromocode'),
          // TODO: @danealton перенести метод во actions'ы vuex
          promocode: this.checkRegistrationPromocode,
        },
      };
    }

    return {
      merchantEmail: {
        required: requiredValidator,
        email: emailValidator,
      },
      merchantPhone: {
        required: requiredValidator,
        minLength: minLengthValidator(PHONE_MIN_LENGTH),
      },
    };
  },
  computed: {
    isInvalidForm() {
      return this.$v.$invalid;
    },
  },
  methods: {
    getFieldLabel(field, label) {
      const fieldLabel = getInputFieldLabel(field, label);
      const fieldLabelTranslation = this.$t(fieldLabel, {
        minLength: PHONE_MIN_LENGTH,
      });

      return fieldLabelTranslation;
    },
    handleShownPromocode() {
      this.isVisiblePromocode = true;

      if (this.$validate.isNotEmptyString(this.promoCode)) {
        this.$v.promoCode.$touch();
      }
    },
    handleSubmit() {
      if (this.isInvalidForm) {
        this.$alert({
          type: 'warning',
          text: 'mistakes.in.form',
        });
        return;
      }

      this.$emit('submit', {
        email: this.merchantEmail,
        phone: this.merchantPhone,
        promoCode: this.isVisiblePromocode ? this.promoCode : null,
      });

      if (this.isVisiblePromocode) {
        this.$alert({
          type: 'success',
          text: 'notification.registration.promo.code.appled',
        });
      }
    },
    async checkRegistrationPromocode(value) {
      if (this.$validate.isEmptyString(value)) {
        return;
      }

      try {
        await checkPromocode(apiCommon, value);
        return true;
      } catch {
        return false;
      }
    },
  },
};
</script>

<style lang="sass" module>
  .placeholder
    +size(100%, 40px)
    +font-type(medium, 14, 40)
    +flexbox
    +justify-content(center)
    +align-items(center)
    border: 1px dashed var(--color-white)
    border-radius: 100px
    cursor: pointer

  .submit
    margin-top: 20px

    +respond-to('medium')
      margin-top: 50px
      padding-right: 235px
</style>
